import { useEffect, useState } from 'react';
import { fetchVenuePosts, type PublicVenuePost } from './postApi';
import './venue-posts.css';

function shortDate(value: string | null): string {
  if (!value) return '';
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return '';
  return new Intl.DateTimeFormat('ru-RU', { day: 'numeric', month: 'short' }).format(parsed);
}

type VenuePostsSectionProps = {
  venueId: string;
  referenceMode?: boolean;
  workspaceMode?: boolean;
  onShowAll?: () => void;
};

export function VenuePostsSection({
  venueId,
  referenceMode = false,
  workspaceMode = false,
  onShowAll,
}: VenuePostsSectionProps) {
  const [posts, setPosts] = useState<PublicVenuePost[]>([]);
  const [page, setPage] = useState(1);
  const [lastPage, setLastPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [retryKey, setRetryKey] = useState(0);
  const [expanded, setExpanded] = useState(false);

  useEffect(() => {
    const controller = new AbortController();
    let active = true;
    const perPage = workspaceMode ? 12 : referenceMode ? 6 : 3;

    setLoading(true);
    setPosts([]);
    setPage(1);
    setLastPage(1);
    setExpanded(workspaceMode);
    setError(null);

    const load = async () => {
      try {
        const first = await fetchVenuePosts(venueId, 1, perPage, controller.signal);
        if (!active) return;

        let collected = first.data;
        let currentPage = 1;
        let finalPage = first.meta.last_page;

        if (workspaceMode) {
          while (currentPage < finalPage && !controller.signal.aborted) {
            currentPage += 1;
            const response = await fetchVenuePosts(venueId, currentPage, perPage, controller.signal);
            collected = [
              ...collected,
              ...response.data.filter((post) => !collected.some((old) => old.id === post.id)),
            ];
            finalPage = response.meta.last_page;
          }
        }

        if (!active) return;
        setPosts(collected);
        setPage(currentPage);
        setLastPage(finalPage);
      } catch {
        if (!controller.signal.aborted && active) setError('Не удалось загрузить посты заведения');
      } finally {
        if (active) setLoading(false);
      }
    };

    void load();

    return () => {
      active = false;
      controller.abort();
    };
  }, [referenceMode, retryKey, venueId, workspaceMode]);

  const loadMore = async () => {
    if (loadingMore || page >= lastPage) return;
    const nextPage = page + 1;
    setLoadingMore(true);
    setError(null);
    try {
      const response = await fetchVenuePosts(venueId, nextPage, referenceMode ? 6 : 3);
      setPosts((current) => [
        ...current,
        ...response.data.filter((post) => !current.some((old) => old.id === post.id)),
      ]);
      setPage(nextPage);
      setLastPage(response.meta.last_page);
    } catch {
      setError('Не удалось загрузить следующую страницу постов');
    } finally {
      setLoadingMore(false);
    }
  };

  const expandReferencePosts = async () => {
    if (onShowAll) {
      onShowAll();
      return;
    }

    if (expanded) {
      setExpanded(false);
      return;
    }

    setLoadingMore(true);
    setError(null);
    try {
      let nextPage = page;
      let finalPage = lastPage;
      let currentPosts = posts;
      while (nextPage < finalPage) {
        nextPage += 1;
        const response = await fetchVenuePosts(venueId, nextPage, 6);
        currentPosts = [
          ...currentPosts,
          ...response.data.filter((post) => !currentPosts.some((old) => old.id === post.id)),
        ];
        finalPage = response.meta.last_page;
      }
      setPosts(currentPosts);
      setPage(nextPage);
      setLastPage(finalPage);
      setExpanded(true);
    } catch {
      setError('Не удалось загрузить все посты');
    } finally {
      setLoadingMore(false);
    }
  };

  if (!loading && !error && posts.length === 0 && !workspaceMode) return null;

  const modeClass = referenceMode
    ? ` venue-posts--reference${workspaceMode ? ' venue-posts--workspace is-expanded' : expanded ? ' is-expanded' : ''}`
    : '';

  return (
    <section className={`venue-detail-section venue-posts${modeClass}`} aria-labelledby="venue-posts-heading">
      <div className="venue-detail-compact-section-heading">
        <h2 id="venue-posts-heading">Посты заведения</h2>
        {referenceMode && !workspaceMode && !loading && posts.length > 0 && (Boolean(onShowAll) || posts.length > 2 || lastPage > 1) && (
          <button className="venue-posts__all" type="button" disabled={loadingMore} onClick={() => void expandReferencePosts()}>
            {loadingMore ? 'Загружаем…' : onShowAll ? 'Все посты' : expanded ? 'Свернуть' : 'Все посты'}
          </button>
        )}
      </div>

      {loading ? (
        <div className="venue-detail-compact-inline-state">Загружаем посты…</div>
      ) : error && posts.length === 0 ? (
        <div className="venue-detail-compact-inline-state is-error">
          <span>{error}</span>
          <button type="button" onClick={() => setRetryKey((value) => value + 1)}>Повторить</button>
        </div>
      ) : posts.length === 0 && workspaceMode ? (
        <div className="venue-owner-empty">
          <strong>Постов пока нет</strong>
          <span>Здесь появится контент заведения.</span>
        </div>
      ) : (
        <div className="venue-posts__list">
          {posts.map((post) => {
            const cover = post.cover?.thumbnail_url ?? post.cover?.url ?? null;
            return (
              <article className="venue-posts__card" key={post.id}>
                <button
                  className={`venue-posts__button${cover ? ' has-cover' : ''}`}
                  type="button"
                  onClick={() => window.location.assign(`/posts/${encodeURIComponent(post.id)}`)}
                >
                  {cover && <img className="venue-posts__cover" src={cover} alt="" loading="lazy" />}
                  <div className="venue-posts__content">
                    <div className="venue-posts__topline">
                      <strong className="venue-posts__title">{post.title}</strong>
                      <span className="venue-posts__date">{shortDate(post.published_at)}</span>
                    </div>
                    <p className="venue-posts__body">{post.body}</p>
                  </div>
                </button>
              </article>
            );
          })}
        </div>
      )}

      {!referenceMode && !loading && page < lastPage && posts.length > 0 && (
        <button className="button button--secondary venue-posts__more" type="button" disabled={loadingMore} onClick={() => void loadMore()}>
          {loadingMore ? 'Загружаем…' : 'Показать ещё'}
        </button>
      )}
      {error && posts.length > 0 && !workspaceMode && (
        <div className="venue-detail-compact-inline-state is-error">
          <span>{error}</span>
          <button type="button" disabled={loadingMore} onClick={referenceMode ? () => void expandReferencePosts() : () => void loadMore()}>Повторить</button>
        </div>
      )}
    </section>
  );
}
